import * as React from 'react';
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';

import { Flex } from '@common/components/Flex';
import { SPREADSHEET_MODULE_NAME } from '../../constants/shared';
import { SpreadsheetExportType } from '@modules/spreadsheets/dto/spreadsheet/export-type';
import { FormControl } from '@common/components/form-control';
import { Button } from '@common/components/Button';
import { useController, DataExportProps } from './data-export.controller';
import { getTestAttributes } from '@common/utils/tests';

import './data-export.styles.scss';

export const DataExport = (props: DataExportProps) => {
  const { close } = props;

  const {
    settings,
    options,
    selectedSetting,
    handleSettingsChange,
    delimiter,
    handleDelimiterChange,
    selectedOptions,
    handleOptionsChange,
    newlinesOptions,
    shouldIgnoreNewlines,
    selectedNewlinesSetting,
    handleNewlinesSettingsChange,
    submitting,
    handleExport,
    RESTRICTED_NEWLINES,
  } = useController(props);

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="SpreadsheetDetailsDataExport"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close} className="SpreadsheetDetailsDataExport__header">
        <ModalTitle {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'export-data', 'heading')} >
          {I18n.t('Export Data')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody className="SpreadsheetDetailsDataExport__content">
        <Flex alignItems="flex-start">
          <div className="SpreadsheetDetailsDataExport__content__sections">
            <section className="SpreadsheetDetailsDataExport__content__section">
              <span className="SpreadsheetDetailsDataExport__content__section__title">{I18n.t('Settings')}</span>

              {settings.map((x) => (
                <Flex key={x.id} alignItems="center" justify="space-between">
                  <label className="SpreadsheetDetailsDataExport__content__section__item">
                    <input 
                      {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'export-data-setting', 'radio', x.id)}
                      type="radio" 
                      value={x.id}
                      checked={selectedSetting === x.id}
                      onChange={handleSettingsChange} 
                    />
                    {x.label}
                  </label>
                  {x.id === selectedSetting && x.id === SpreadsheetExportType.txtOther && (
                    <Flex className="SpreadsheetDetailsDataExport__content__delimiter" alignItems="center">
                      <span>{I18n.t('Delimiter')}:&nbsp;</span>
                      <FormControl 
                        {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'export-data-delimiter', 'input')}
                        className="SpreadsheetDetailsDataExport__content__delimiter__input"
                        value={delimiter}
                        onChange={handleDelimiterChange}
                      />
                    </Flex>
                  )}
                </Flex>
              ))}
            </section>

            <section className="SpreadsheetDetailsDataExport__content__section">
              <span className="SpreadsheetDetailsDataExport__content__section__title">{I18n.t('Newline Handling for Text Fields')}</span>

              {newlinesOptions.map((x) => (
                <label key={x.id} className="SpreadsheetDetailsDataExport__content__section__item">
                  <input 
                    disabled={shouldIgnoreNewlines || RESTRICTED_NEWLINES[selectedSetting]?.includes(x.id)}
                    {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'export-newlines-setting', 'radio', x.id)}
                    type="radio" 
                    value={x.id}
                    checked={selectedNewlinesSetting === x.id}
                    onChange={handleNewlinesSettingsChange} 
                  />
                  {x.label}
                </label>
              ))}
            </section>

            <section className="SpreadsheetDetailsDataExport__content__section">
              <span className="SpreadsheetDetailsDataExport__content__section__title">{I18n.t('Options')}</span>

              {options.map((x) => (
                <label key={x.id} className="SpreadsheetDetailsDataExport__content__section__item">
                  <input 
                    {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'export-data-option', 'checkbox', x.id)}
                    type="checkbox" 
                    value={x.id}
                    checked={selectedOptions.includes(x.id)}
                    onChange={handleOptionsChange} 
                  />
                  {x.label}
                </label>
              ))}
            </section>
          </div>
          <Button
            className="SpreadsheetDetailsDataExport__content__submit-btn"
            disabled={submitting || (selectedSetting === SpreadsheetExportType.txtOther && !delimiter)}
            {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'export', 'button')}
            onClick={handleExport}
          >
            {submitting ? I18n.t('Exporting') : I18n.t('Export')}
          </Button>
        </Flex>
      </ModalBody>
    </Modal>
  );
};